import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { imgFactionWeapon } from "@/shared/assets/img";
import { Search2Icon, UsersIcon } from "@/shared/icons";
import { TextInput } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import FactionTabsRow from "@/domains/faction/components/FactionTabsRow";
import FactionWarehouseCard from "@/domains/faction/components/FactionWarehouseCard";

type Props = {
  faction: FactionEnum;
};

export default function FactionWarehouseContainer({ faction }: Props) {
  const { t } = useTranslation("faction");

  const tabs = useMemo(() => ["all", "ammo", "weapons", "food", "other"], []);

  const [inputValue, setInputValue] = useState("");
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const fakeData = useMemo(
    () => [
      { id: 1, src: imgFactionWeapon, name: "Assault SMG", count: 12354, type: "weapon" },
      { id: 2, src: imgFactionWeapon, name: "9mm Ammo", count: 8765, type: "ammo" },
      { id: 3, src: imgFactionWeapon, name: "Sniper Rifle", count: 42, type: "weapon" },
      { id: 4, src: imgFactionWeapon, name: "Canned Food", count: 315, type: "food" },
      {
        id: 5,
        src: imgFactionWeapon,
        name: "Grenade Launcher",
        count: 23,
        type: "weapon",
      },
      { id: 6, src: imgFactionWeapon, name: "7.62mm Ammo", count: 5432, type: "ammo" },
      { id: 7, src: imgFactionWeapon, name: "SMG", count: 65, type: "weapon" },
      { id: 8, src: imgFactionWeapon, name: "Bottled Water", count: 287, type: "food" },
      { id: 9, src: imgFactionWeapon, name: "Combat Knife", count: 234, type: "weapon" },
      {
        id: 10,
        src: imgFactionWeapon,
        name: "12 Gauge Shells",
        count: 1987,
        type: "ammo",
      },
      {
        id: 11,
        src: imgFactionWeapon,
        name: "Submachine Gun",
        count: 76,
        type: "weapon",
      },
      { id: 12, src: imgFactionWeapon, name: "Energy Bar", count: 156, type: "food" },
      { id: 13, src: imgFactionWeapon, name: "Revolver", count: 128, type: "weapon" },
      { id: 14, src: imgFactionWeapon, name: ".45 ACP Ammo", count: 3241, type: "ammo" },
      { id: 15, src: imgFactionWeapon, name: "Grenade", count: 456, type: "weapon" },
      { id: 16, src: imgFactionWeapon, name: "MRE Pack", count: 89, type: "food" },
      { id: 17, src: imgFactionWeapon, name: "SMG Pro", count: 89, type: "weapon" },
      { id: 18, src: imgFactionWeapon, name: "5.56mm Ammo", count: 7654, type: "ammo" },
      {
        id: 19,
        src: imgFactionWeapon,
        name: "Tactical Shotgun",
        count: 27,
        type: "weapon",
      },
      { id: 20, src: imgFactionWeapon, name: "Medical Kit", count: 43, type: "food" },
      { id: 21, src: imgFactionWeapon, name: "Grenade MK2", count: 38, type: "weapon" },
    ],
    [],
  );

  const filteredData = useMemo(() => {
    const searchValue = inputValue.trim().toLowerCase();

    const tabToType: Record<string, string | null> = {
      all: null,
      ammo: "ammo",
      weapons: "weapon",
      food: "food",
      other: "other",
    };

    const requiredType = tabToType[activeTab] ?? null;

    return fakeData.filter((item) => {
      const matchesTab = requiredType ? item.type === requiredType : true;
      const matchesQuery = searchValue ? item.name.toLowerCase().includes(searchValue) : true;
      return matchesTab && matchesQuery;
    });
  }, [activeTab, fakeData, inputValue]);

  return (
    <div className="flex flex-col h-full p-6">
      <FactionTabsRow
        title={t("faction.titles.warehouse")}
        Icon={UsersIcon}
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        faction={faction}
        i18Path="faction.warehouseTabs"
      />
      <TextInput
        value={inputValue}
        onChange={setInputValue}
        maxLength={40}
        type="text"
        inputClassName="h-9.5 text-14"
        placeholder={t("faction.searchForItems")}
        icon={<Search2Icon className="size-4.5" />}
        className="mb-4"
      />
      <div className="relative grow inset-0">
        {filteredData?.length > 0 ? (
          <div className="absolute top-0 left-0 bottom-0 grid grid-cols-6 auto-rows-min gap-[0.9375rem] pb-5 overflow-x-clip w-[calc(100%+0.9375rem)] pr-[0.6875rem] overflow-y-auto [--scrollbar-track-bg:#181818] [--scrollbar-thumb-bg:#434242] [scrollbar-gutter:stable]">
            {filteredData.map((item) => (
              <FactionWarehouseCard
                key={item.name}
                src={item.src}
                name={item.name}
                count={item.count}
              />
            ))}
          </div>
        ) : (
          <div className="size-full flex justify-center items-center text-16 font-bold text-white/50 leading-none uppercase">
            {t("faction.noItems")}
          </div>
        )}
      </div>
      <div className="relative pointer-events-none">
        <div className="absolute z-2 -top-14 left-0 w-full h-14 bg-gradient-to-t from-dark-900 to-transparent" />
        <div className="absolute z-2 -top-12 left-0 w-full h-12 bg-gradient-to-t from-dark-900 to-transparent" />
      </div>
    </div>
  );
}
